import { useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

export const useSystemAdmin = (callerUserId) => {
  const [users, setUsers] = useState([]);
  const [assignSearchResults, setAssignSearchResults] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchUsers = useCallback(async (search = '') => {
    if (!callerUserId) return [];
    try {
      const res = await axios.get(`${API}/api/admin/users`, {
        params: { caller_user_id: callerUserId, search, limit: 2000 },
      });
      return res.data.users || [];
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load users');
      return [];
    }
  }, [callerUserId]);

  const searchUsers = useCallback(async (search = '') => {
    setLoading(true);
    setError('');
    const list = await fetchUsers(search);
    setUsers(list);
    setLoading(false);
  }, [fetchUsers]);

  const searchAssignUsers = useCallback(async (search = '') => {
    setError('');
    const list = await fetchUsers(search);
    setAssignSearchResults(list);
  }, [fetchUsers]);

  const loadSupervisors = useCallback(async () => {
    if (!callerUserId) return;
    try {
      const res = await axios.get(`${API}/api/admin/supervisors`, {
        params: { caller_user_id: callerUserId },
      });
      setSupervisors(res.data.supervisors || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load supervisors');
    }
  }, [callerUserId]);

  const loadAssignedUsers = useCallback(async (supervisorId) => {
    if (!callerUserId || !supervisorId) {
      setAssignedUsers([]);
      return;
    }
    try {
      const res = await axios.get(
        `${API}/api/admin/supervisors/${supervisorId}/users`,
        { params: { caller_user_id: callerUserId } },
      );
      setAssignedUsers(res.data.users || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load assignments');
    }
  }, [callerUserId]);

  const updateUserType = useCallback(async (targetUserId, newType) => {
    try {
      await axios.patch(
        `${API}/api/admin/users/${targetUserId}/type`,
        { caller_user_id: callerUserId, user_type: newType },
      );
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || 'Failed' };
    }
  }, [callerUserId]);

  const getUsersSupervisor = useCallback(async (targetUserId) => {
    try {
      const res = await axios.get(
        `${API}/api/admin/users/${targetUserId}/supervisor`,
        { params: { caller_user_id: callerUserId } },
      );
      return { success: true, supervisor: res.data.supervisor };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || 'Failed' };
    }
  }, [callerUserId]);

  const assignUser = useCallback(async (userId, supervisorId) => {
    try {
      await axios.post(
        `${API}/api/admin/assignments`,
        { caller_user_id: callerUserId, user_id: userId, supervisor_id: supervisorId },
      );
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || 'Failed' };
    }
  }, [callerUserId]);

  const lookupOrganization = useCallback(async (email) => {
    if (!callerUserId || !email) return null;
    try {
      const res = await axios.get(`${API}/api/admin/organizations/lookup`, {
        params: { caller_user_id: callerUserId, email },
      });
      return res.data.organization;
    } catch (e) {
      return null;
    }
  }, [callerUserId]);

  const createUser = useCallback(async (payload) => {
    try {
      const res = await axios.post(`${API}/api/admin/users`, {
        caller_user_id: callerUserId,
        ...payload,
      });
      return { success: true, data: res.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || 'Failed' };
    }
  }, [callerUserId]);

  const unassignUser = useCallback(async (userId, supervisorId) => {
    try {
      await axios.delete(`${API}/api/admin/assignments`, {
        data: { caller_user_id: callerUserId, user_id: userId, supervisor_id: supervisorId },
      });
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || 'Failed' };
    }
  }, [callerUserId]);

  return {
    users, assignSearchResults, supervisors, assignedUsers,
    loading, error, setError,
    searchUsers, searchAssignUsers, loadSupervisors, loadAssignedUsers,
    lookupOrganization, createUser,
    updateUserType, getUsersSupervisor, assignUser, unassignUser,
  };
};
